import React from 'react';
import { Link } from 'react-router-dom';
import { formatCurrency } from '../../../utils/baseUtils';
import { cn } from '../../../components/ui/_cn';
import { availabilityCell, platformCell, utcDateCell, zonedDateTimeCell } from '../../finance/pages/finance-cells';
import FinButton from './fin-button';
import { FinTableSkeleton } from './fin-skeleton';

// Overview's Recent orders card (spec 2026-09-21 §4.3). Status answers "has
// the money landed" — Available / Pending / — — not "Paid": a column reading
// Paid on every row of a paid-orders list carries no information, and Order
// Reports answers the same question with the same badge.
//
// Only the order number is clickable, drawn as the design draws plain text
// (ink, underline on hover). A row with no id behind it stays text.
// Below `sm` Created and Available are not drawn (owner, 2026-09-22: on a
// phone Net fell behind a sideways scroll): Order · Status · Net.
//
// The owner (2026-09-25) asked for Platform after Order. On this compact
// card — about 410–490px wide beside its neighbour — a sixth column pushed
// Net behind a sideways scroll at common laptop widths. The owner already
// kept Net out of a sideways scroll on phones (2026-09-22, above); the same
// reasoning makes Platform a muted line under the order number, at every
// width, in the same two-line rhythm as Created. Order Reports and
// Transactions keep it as a column of its own.
const GRID = 'grid grid-cols-[80px_minmax(0,1fr)_72px] sm:grid-cols-[80px_84px_minmax(0,1fr)_90px_72px] gap-x-3';
const HEADERS = ['Order', 'Status', 'Created', 'Available', 'Net'];
const PHONE_HIDDEN = new Set(['Created', 'Available']);
const PHONE_HIDDEN_CLS = 'hidden sm:block';

// Side by side with Payout activity only at `lg` (Overview's `lg:grid-cols-2`);
// in one column the card hugs its rows. CENTRED keeps its height at every width.
const BODY = 'lg:min-h-[320px]';
const CENTRED = 'flex min-h-[320px] items-center justify-center gap-3 text-center';

const RecentOrdersTable = ({ rows = [], loading = false, error = '', onRetry, empty }) => {
  if (error) {
    return (
      <div className={CENTRED}>
        <p className="text-[13px] text-fin-red">{error}</p>
        <FinButton onClick={onRetry}>Retry</FinButton>
      </div>
    );
  }
  if (loading && rows.length === 0) {
    return (
      <div className={BODY}>
        <FinTableSkeleton rows={6} />
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <div className="sm:min-w-[420px]">
        <div role="table" aria-label="Recent orders">
          <div className={cn(GRID, 'border-b border-fin-border pb-2 pt-4 text-[12px] font-medium text-fin-faint')} role="row">
            {HEADERS.map((h) => (
              <div
                key={h}
                role="columnheader"
                className={cn(h === 'Net' && 'text-right', PHONE_HIDDEN.has(h) && PHONE_HIDDEN_CLS) || undefined}
              >
                {h}
              </div>
            ))}
          </div>
          {/* rowgroup: the only element ARIA lets sit between role=table and
              its rows, and the one place the body's min-height belongs. */}
          <div role="rowgroup" className={rows.length > 0 ? BODY : undefined}>
            {rows.map((row, index) => (
              <div
                key={row.orderId || row.orderNumber || index}
                role="row"
                className={cn(GRID, 'items-center py-3 text-[13.5px]', index < rows.length - 1 && 'border-b border-fin-hair')}
              >
                <div role="cell" className="flex flex-col items-start leading-tight">
                  {row.orderId ? (
                    <Link to={`/dashboard/orders/${row.orderId}`} className="font-semibold tabular-nums text-fin-ink hover:underline">
                      {`#${row.orderNumber}`}
                    </Link>
                  ) : (
                    <span className="font-semibold tabular-nums text-fin-ink">{`#${row.orderNumber}`}</span>
                  )}
                  <span className="text-[12px]">{platformCell(row.platform)}</span>
                </div>
                <div role="cell">{availabilityCell(row.fundsAvailableOn)}</div>
                <div role="cell" className={PHONE_HIDDEN_CLS}>{zonedDateTimeCell(row.createdAt, row.timezoneId)}</div>
                <div role="cell" className={PHONE_HIDDEN_CLS}>{utcDateCell(row.fundsAvailableOn)}</div>
                <div role="cell" className="text-right font-bold tabular-nums text-fin-ink">{formatCurrency(row.netPayout)}</div>
              </div>
            ))}
          </div>
        </div>
        {/* Sibling of role=table, not a child: a <p> is disallowed content
            under table/grid and browsers may prune it from the a11y tree
            (spec 2026-09-21 review, final wave item 1/6/16). */}
        {rows.length === 0 ? (
          <div className={CENTRED}>
            <p className="text-[13px] text-fin-muted">{empty}</p>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default RecentOrdersTable;
